import {
  Component,
  Input,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  ElementRef,
  Renderer2,
  ViewChild,
  AfterViewChecked
} from '@angular/core';
import { WizTranslatableComponent } from '../../core/wiz-translatable-component';
import { WizStringUtils } from '@wizeoo/akm-core-lib';

@Component({
  selector: 'wiz-label',
  template: `<label #wizLabelComponent id='wizLabelComponent' class="wiz-label"
  [wizTooltip]="translatedTooltip"
  [tooltipEnableFunc]=this.manageTooltip.bind(this)
  [innerHTML]="translatedLabel"></label>`,
  styleUrls: ['./wiz-label.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WizLabel extends WizTranslatableComponent implements AfterViewChecked {

  /** Constructor
   * @param cdref: ChangeDetectorRef
   * @param elementRef: the dom of the component
   * @param renderer: object that will allow the interaction between the component and the DOM
   */
  constructor(cdref: ChangeDetectorRef, elementRef: ElementRef,  renderer: Renderer2) {
    super(cdref, elementRef, renderer);
    this.checkStyle();
  }

  @ViewChild('wizLabelComponent') wizLabelComponent: ElementRef;

  /** skinning */
  public disabledClassName: string = 'disabled-class'; // class css pour skin "disabled"

  /** flag pour lancer le refresh, sur modif de params */
  private _labelIsDirty: boolean = false;


  /**
   * Defines how the label should behave when the label can't be fully displayed
   * - truncate : This keyword will display an ellipsis ('…') to represent clipped text.
   *              The text will be on one line.
   * - wordWrap : This keyword will truncate the text at the limit of the content area.
   *              Lines are broken as necessary to fill line boxes.
   */
  public get wizTextBehavior(): 'truncate' | 'wordWrap' {
    return  this.getWizTextBehavior();
  }
  @Input() public set wizTextBehavior(behav: 'truncate' | 'wordWrap') {
    this.setWizTextBehavior(behav);
  }
  protected _wizTextBehavior: 'truncate' | 'wordWrap';

  protected getWizTextBehavior(): 'truncate' | 'wordWrap' {
    return this._wizTextBehavior;
  }

  protected setWizTextBehavior(behav: 'truncate' | 'wordWrap'): void {
    if (behav === 'truncate' || behav === 'wordWrap') {
      this._wizTextBehavior = behav;
    } else {
      this._wizTextBehavior = null;
    }
    this._labelIsDirty = true;
    this.cdref.markForCheck();
  }

  /**
   * defines how hidden overflow content is signaled to users
   * this property can be 'clip',  'ellipsis' ('…'), or a custom string like '-'.
   */
  @Input()
  public set wizTextOverflow(wto: string) {
    this.setWizTextOverflow(wto);
  }
  protected _wizTextOverflow: string = 'ellipsis';

  protected setWizTextOverflow(wto: string): void {
    this._wizTextOverflow = wto;
    this.cdref.markForCheck();
  }

  /**
   * Defines the labels width.
   * This property won't affect the component as the width is set on the component by the wiz-width.directive.
   * This property is only used to calculate how and where to break the text
   */
  @Input()
  public set width(v: string) {
    this.setWidth(v);
  }
  public get width(): string {
    return this.getWidth();
  }
  protected _width: string;

  protected setWidth(value: string): void {
    this._width = value;
    this.cdref.markForCheck();
  }

  protected getWidth(): string {
    return this._width;
  }

  /**
   * Defines the labels height.
   * This property won't affect the component as the height is set on the component by the wiz-height.directive.
   * This property is only used to calculate how and where to break the text
   */
  @Input()
  public set height(v: string) {
    this._height = v;
    this.cdref.markForCheck();
  }
  public get height(): string {
    return this._height;
  }
  protected _height: string;

  protected setHeight(value: string): void {
    this._height = value;
    this.cdref.markForCheck();
  }

  protected getHeight(): string {
    return this._height;
  }

  /**
   * the number of line that are allowed
   * this property is used to calculate the number of line to display when the textBehavior is wordWrap.
   */
  @Input()
  public set wizNbLine(nbLine: number) {
    this.setWizNbLine(nbLine);
  }
  public get wizNbLine(): number {
    return this.getWizNbLine();
  }
  protected _wizNbLine: number = -1;

  protected setWizNbLine(nbLine: number): void {
    if (typeof nbLine === 'number' && nbLine > 0 ) {
      this._wizNbLine = nbLine;
    } else {
      this._wizNbLine = -1;
    }
    this._labelIsDirty = true;
    this.cdref.markForCheck();
  }

  protected getWizNbLine(): number {
    return this._wizNbLine;
  }

  /**
   * application externe d'un style complémentaire
   */
  @Input()
  public set wizDynamicStyle(stylename: string) {
    this.setWizDynamicStyle(stylename);
  }
  public get wizDynamicStyle(): string {
    return this.getWizDynamicStyle();
  }
  private _wizDynamicStyle: string = null;
  private _previousDynamicStyle: string = null; // le style dynamique précédent, pour l'enlever

  protected setWizDynamicStyle(stylename: string): void {
    // if (stylename) {
    this._wizDynamicStyle = stylename;
    // }
    this._labelIsDirty = true;
    this.cdref.markForCheck();
  }

  protected getWizDynamicStyle(): string {
    return this._wizDynamicStyle;
  }

  /**
   * override du wizDisabled pour afficher un message d'erreur
   */
  public set wizDisabled(v: boolean) {
    this.setWizDisabled(v);
    if(this.wizLabel && v === true){
      console.error('Use the ngClass to display a disabled style');
    }
  }

  /**
   * on ne veux pas de tooltip sur ce compo (la méca interne ne fonctionne qu'a l'init, pas sur les éléms dynamiques)
   */
  @Input()
  public set wizDisableTooltip(value: boolean) {
     this._wizDisableTooltip = value;
  }
  public get wizDisableTooltip(): boolean {
     return this._wizDisableTooltip;
  }
  protected _wizDisableTooltip: boolean = false;

  /**
   * on ne veux toujours le tooltip
   */
   @Input()
   public set wizForceTooltip(value: boolean) {
      this._wizForceTooltip = value;
   }
   public get wizForceTooltip(): boolean {
      return this._wizForceTooltip;
   }
   protected _wizForceTooltip: boolean = false;
 

  // ####
  // ####
  // #### mécanique interne ####
  // ####
  // ####

  /** sur markForCheck, refresh éventuel */
  public ngAfterViewChecked(): void {
    if (this._labelIsDirty) {
      this._labelIsDirty = false;
      this.setBehavior();
    }
  }

  /**
   * reset the style of the component
   * this method is called at every begining of the setBehavior method
   */
  private resetStyle(): void {
    this.renderer.setStyle(this.wizLabelComponent.nativeElement, 'display', 'inline-block');
    this.renderer.removeStyle(this.wizLabelComponent.nativeElement, 'overflow');
    this.renderer.setStyle(this.wizLabelComponent.nativeElement, 'white-space', 'nowrap');
    this.renderer.setStyle(this.wizLabelComponent.nativeElement, 'max-height', '100%');
    this.renderer.removeStyle(this.wizLabelComponent.nativeElement, '-webkit-line-clamp');
    this.renderer.removeStyle(this.wizLabelComponent.nativeElement, '-webkit-box-orient');
    this.renderer.removeStyle(this.wizLabelComponent.nativeElement, '-webkit-line-clamp');
  }

  /** application du style effectif (disabled, custom) */
  public checkStyle(): void {
    if (this._previousDynamicStyle !== this.wizDynamicStyle) {// il existe un style dynamique, différent de l'actuel
      if (!!this._previousDynamicStyle) {
        this.renderer.removeClass(this.elementRef.nativeElement, this._previousDynamicStyle); // suppression de l'ancienne classe de style
      }
      if (!!this.wizDynamicStyle) {
        this.renderer.addClass(this.elementRef.nativeElement, this.wizDynamicStyle); // ajout de la nouvelle classe de style
      }
      this._previousDynamicStyle = this.wizDynamicStyle // alimentation du style dynamic "actif"
    }
  }

  /**
   * changes the label behavior
   * if properties width and wizTextBehavior are given, it change the display of the label
   * either it clip, ellipsis or wrap the text
   */
  private setBehavior(): void {
    this.resetStyle(); // reset des styles avant application
    this.checkStyle(); // application disabled / style custom

    if (!!this.wizTextBehavior) { // un comportement est défini
      // the overflow of the label is  set to hidden, this way it will only be displayed inside the given width
      this.renderer.setStyle(this.wizLabelComponent.nativeElement, 'overflow', 'hidden');
      // if the text has to be truncated
      if (this.wizTextBehavior === 'truncate') {
        this.truncateLabelText();
        // this.manageTooltip();
        // if the text has to be wrapped
      } else if (this.wizTextBehavior === 'wordWrap') {
        // console.log('wordwrap');
        this.wordWrapText();
      }
    } else {
      if (this._wizNbLine > 0) {// if a nbline is given we set the textBehavior as wordWrap
        this.wordWrapText();
      } else { // clip
        this.renderer.setStyle(this.wizLabelComponent.nativeElement, 'text-overflow', 'clip');
        // this.manageTooltip();
      }
    }
  }

  /**
   * truncate the text of the label
   * this methode is called when the component width are set and wizTextBehavior equals to 'truncate'
   */
  private truncateLabelText(): void {
    // text overflow ellipsis add 3 dots in the end of truncated text
    this.renderer.setStyle(this.wizLabelComponent.nativeElement, 'text-overflow', this._wizTextOverflow);

    // white-space nowrap make the line stand in one line
    this.renderer.setStyle(this.wizLabelComponent.nativeElement, 'white-space', 'nowrap');

    // needed to have the truncated text with the ellipsis
    this.renderer.setStyle(this.wizLabelComponent.nativeElement, 'display', 'inline-block');
  }

  /**
   * break the label text and display nbLine lines
   * this methode is called when the width are set and wizTextbehavior equals to 'wordWrap'
   * it calculate the number of lines allowed
   */
  private wordWrapText(): void {
    // white-space normal make line breaks naturally to fill the boxes
    this.renderer.setStyle(this.wizLabelComponent.nativeElement, 'white-space', 'normal');

    // this line height is the multiplication of the line height and the font size
    let lineheight =  parseInt(window.getComputedStyle(this.wizLabelComponent.nativeElement, null)
                                                            .getPropertyValue('line-height').replace(/px/, ' '), 10);
    // in case of the line-height is not defined, we calculate it manual
    if (!lineheight) {
      const fontSize =  parseInt(window.getComputedStyle(this.wizLabelComponent.nativeElement, null)
                                                              .getPropertyValue('font-size').replace(/px/, ' '), 10);
      // 1.5 is the default line-height
      lineheight = fontSize * 1.5;
    }
    // the maxHeight wheither the wiznbline is given or not
    const maxHeight = this._wizNbLine > 0 ? lineheight * this._wizNbLine : this.elementRef.nativeElement.offsetHeight;
    // intHeight is equal to the height if it is given, otherwise it takes its content height.
    const intHeight = (this.height && this.height.endsWith('px')) ?
                                parseInt(this.height.replace(/px/, ' '), 10) : this.elementRef.nativeElement.offsetHeight;

    // if the maxHeight is greater than the height of the the wizLabel (when height is given),
    // heightLimit becomes the given height
    let heightLimit =  (this._wizNbLine > 0 && maxHeight < intHeight) ? maxHeight : this.height ? intHeight : maxHeight;
    // the maximum number of lines allowed
    const maxline = heightLimit / lineheight;

    if (this._wizNbLine > 0) {
      if (intHeight < maxHeight) {
        // allows you to calculate the heightLimit when the last line appears halfway through.
        heightLimit = Math.floor(maxline) * lineheight;
      }
      // add ellipsis in the end of the last line corresponding to maxline
      this.renderer.setStyle(this.wizLabelComponent.nativeElement, '-webkit-line-clamp', Math.floor(maxline));
      // helps for the ellipsis at the last line
      this.renderer.setStyle(this.wizLabelComponent.nativeElement, 'display', '-webkit-box');
      // helps for the ellipsis at the last line
      this.renderer.setStyle(this.wizLabelComponent.nativeElement, '-webkit-box-orient', 'vertical');
      // set the number of allowed lines
      this.renderer.setStyle(this.wizLabelComponent.nativeElement, 'max-height', heightLimit + 'px');
    } else {
      this.renderer.setStyle(this.wizLabelComponent.nativeElement, 'max-height', '100%');
    }
  }

  /**
   * Gestion de l'affichage du tooltip
   * si le tooltipLabel est donné, le tooltip s'affiche à chaque arret de la souris sur le label
   * sinon si le texte est partiellement visible, on affiche le libellé traduit dans le tooltip
   * sinon le tootltip n'appartait pas
   */
  public manageTooltip(): boolean  {
    if (this.wizForceTooltip) { // forcé
      return true;
    }
    
    if (this.wizDisableTooltip) { // neutralisation
      return false;
    }
    let result: boolean = false;
    if (WizStringUtils.isNotEmpty(this.translatedTooltip)) {
      // console.log('label tooltip', this.translatedTooltip, this.wizLabelComponent.nativeElement.scrollWidth, this.wizLabelComponent.nativeElement.offsetWidth);
      if (this.wizLabelComponent.nativeElement.scrollWidth > this.wizLabelComponent.nativeElement.offsetWidth) {
        result = true;
      } else {
        result = false;
      }
    } else {
      result = false;
    }
    return result;
  }

}
